import { toggleLoader, toggleModal } from 'state/actions';
import axios from 'axios';
import store from 'state/store/configureStore';
import _ from 'lodash';
import { logOut } from './services/auth.service.js';
import {
  getToken,
  refreshAccessTokenAndStoreInCookie
} from './services/token.service';
import history from 'common/RouterHistory';
import BrowserStorageAccessor from './utils/BrowserStorageAccessor';
import { SESSION_STORAGE_KEYS } from './constants/constants';
import { tz } from 'moment-timezone';
import { API_GATEWAY_URI, LINB_API } from './constants/api-urls.js';
import { parseJwt } from './utils/utils.js';
import { organizationSelector } from 'state/selectors/organization.js';
import { isMultiOrgSelector } from 'state/selectors/account.js';
import { createSessionExpiredModalOptions } from 'modals/modal-options-factory.js';
import { AUTH_ERROR } from '@linearb/linjs-common';
import { ENDPOINTS_WITHOUT_LOADER } from './requester.utils.js';

const extractPathName = response => {
  try {
    const { pathname } = new URL(response?.config?.url);
    return pathname;
  } catch (error) {
    window.TrackJS?.console.error(
      'Failed to extract request path name',
      error,
      response
    );
    return '';
  }
};

const shouldTriggerLoader = (url = '') => {
  if (ENDPOINTS_WITHOUT_LOADER.some(PATH => url.includes(PATH))) return false;
  if (url.includes(API_GATEWAY_URI)) return true;

  return false;
};

const MAX_RESPONSE_TIME = 5000;

const onRequest = _.debounce(
  url => {
    if (shouldTriggerLoader(url)) store.dispatch(toggleLoader(true));
  },
  1000,
  { leading: true }
);

const onResponse = _.debounce(
  () => {
    store.dispatch(toggleLoader(false));
  },
  1000,
  { trailing: true }
);

const storeLastLocation = () => {
  const { search, pathname } = _.get(history, 'location', {});
  BrowserStorageAccessor().session.set(
    SESSION_STORAGE_KEYS.REDIRECT_TO,
    `${pathname}${search}`
  );
};

const isMultiOrgTokenInvalid = token => {
  const state = store.getState();
  const isMultiOrg = isMultiOrgSelector(state);

  if (!isMultiOrg) return false;

  const organization = organizationSelector(state);
  const reduxOrgId = Number(_.get(organization, 'id'));

  const parsedToken = parseJwt(token);
  const tokenOrgId = Number(_.get(parsedToken, 'organizationId'));

  if (reduxOrgId && reduxOrgId !== tokenOrgId) {
    return true;
  }

  return false;
};

const isRefreshTokenInvalid = (pathname, response) => {
  return (
    LINB_API.TOKEN.REFRESH.includes(pathname) &&
    response.status === 200 &&
    response.data[AUTH_ERROR.REFRESH_TOKEN.ERROR_KEY_NAME] ===
      AUTH_ERROR.REFRESH_TOKEN.ERROR_MSG
  );
};

const toggleApiPrefix = (currentUrl, currentPrefix) => {
  const url = new URL(currentUrl);
  const newPrefix = currentPrefix === 'api.' ? 'api2.' : 'api.';
  url.hostname = newPrefix + url.hostname.replace(/^api\d?\./, '');
  return url.toString();
};

let requestCounter = 0;
let nConcurrentRequests = 0;
let apiAddressPrefix = 'api.'; // Initialize the prefix

const isLocalhost = () => {
  return window.location.hostname === 'localhost';
};

// Add token interceptor
axios.interceptors.request.use(
  function (config) {
    nConcurrentRequests = nConcurrentRequests + 1;
    config.metadata = {
      startTime: new Date(),
      nConcurrentRequestsPreRequest: nConcurrentRequests
    };

    const token = getToken();
    if (token) {
      if (isMultiOrgTokenInvalid(token)) return logOut();

      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['X-User-Timezone'] = tz(tz.guess()).format('z');
    }

    if (requestCounter === 0) {
      onRequest(config.url);
      ++requestCounter;
    }

    if (!isLocalhost()) {
      config.url = toggleApiPrefix(config.url, apiAddressPrefix);
      apiAddressPrefix = apiAddressPrefix === 'api.' ? 'api2.' : 'api.'; // Toggle the prefix
    }

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

const buildLog = ({ status = '', config = {}, duration } = {}) => {
  const { method = '', url, headers = {}, data } = config;
  const { pathname } = url ? new URL(url) : {};
  return `${status} ${method.toUpperCase()} ${pathname} ${duration}ms [request]: { headers: ${JSON.stringify(
    _.omit(headers, 'Authorization')
  )}, body: ${data} }`;
};

// 401 response interceptor
axios.interceptors.response.use(
  function (response) {
    nConcurrentRequests = nConcurrentRequests - 1;
    const pathname = extractPathName(response);

    if (isRefreshTokenInvalid(pathname, response)) {
      storeLastLocation();
      store.dispatch(toggleModal(createSessionExpiredModalOptions()));
      return;
    }

    _.set(response, 'config.metadata.endTime', new Date());
    response.duration =
      response.config.metadata.endTime - response.config.metadata.startTime;
    const log = buildLog(response);
    window.TrackJS && window.TrackJS.console.info(log);

    if (response.duration > MAX_RESPONSE_TIME) {
      window.DD_LOGS &&
        window.DD_LOGS.logger.warn(`${pathname} slow response time`, {
          duration: response.duration,
          pathname,
          nConcurrentRequests,
          nConcurrentRequestsPreRequest:
            response.config.metadata.nConcurrentRequestsPreRequest
        });
    }

    if (requestCounter === 1) {
      onResponse();
    }

    --requestCounter;
    if (requestCounter < 0) {
      requestCounter = 0;
    }

    return response.data;
  },
  async function (error) {
    nConcurrentRequests = nConcurrentRequests - 1;
    const originalRequest = error.config;
    if (requestCounter === 1) {
      onResponse();
    }

    --requestCounter;
    if (requestCounter < 0) {
      requestCounter = 0;
    }
    if (axios.isCancel(error)) {
      const log = `Request canceled: ${error?.message}`;
      window.TrackJS && window.TrackJS.console.info(log);
      return Promise.reject(error);
    }

    const pathname = extractPathName(error);

    if (originalRequest && originalRequest.metadata) {
      _.set(originalRequest, 'metadata.endTime', new Date());
      error.duration =
        originalRequest.metadata.endTime - originalRequest.metadata.startTime;
    }

    const log = buildLog(error.response);
    window.TrackJS && window.TrackJS.console.info(log);

    const responseCode = _.get(error, 'response.status', 0);

    if (responseCode >= 500) {
      window.DD_LOGS &&
        window.DD_LOGS.logger.error(`${pathname} api response error`, {
          duration: error.duration,
          pathname,
          nConcurrentRequests,
          nConcurrentRequestsPreRequest:
            originalRequest?.metadata?.nConcurrentRequestsPreRequest,
          error: JSON.stringify(_.omit(error, 'config.headers.Authorization'))
        });
    }

    const tykNoAuthField =
      responseCode === 400 &&
      _.get(error, 'response.data.error', null) ===
        'Authorization field missing';
    const tykKeyNotAuthorized =
      responseCode === 403 &&
      _.get(error, 'response.data.error', '').startsWith('Key not authorized');

    if (responseCode === 401 || tykNoAuthField || tykKeyNotAuthorized) {
      if (LINB_API.TOKEN.REFRESH.includes(pathname)) {
        storeLastLocation();
        store.dispatch(toggleModal(createSessionExpiredModalOptions()));
        return;
      }
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const res = await axios.get(LINB_API.TOKEN.REFRESH, {
          withCredentials: true
        });
        const accessToken = refreshAccessTokenAndStoreInCookie(res);
        return replayUnauthorizedRequest(originalRequest, accessToken);
      }
    }

    return Promise.reject(error);
  }
);

const replayUnauthorizedRequest = async (originalRequest, accessToken) => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  return axios(originalRequest);
};

export default axios;
